import _ from 'lodash/lang';

import songService from '../services/songService';
import { getSongChartCoverage } from '../utils/partCharts';
import { buildSongTimeline } from '../utils/songTiming';

/**
 * Whether every part of a song that can carry a chart has one. Only then is its length read off
 * the charts rather than estimated, so only then does a service's length add up to anything.
 * @param {Object} song - A song off the team list, or in full
 * @returns {boolean}
 */
export const isFullyCharted = (song) => {
  const coverage = getSongChartCoverage(song);

  return Boolean(coverage && coverage.total > 0 && coverage.charted === coverage.total);
};

/**
 * How long a full song plays for, timed as Smart Scroll times it. Hidden parts, and their repeats,
 * aren't played.
 * @param {Object} song - The song in full, as fetched
 * @returns {number|null} - Seconds, or null when the song has no BPM to time it with
 */
export const estimateSongSeconds = (song) => {
  // loadDuplicateParts fills the repeats in place, and the song may be a cached copy
  const copy = _.cloneDeep(song);
  const hiddenIds = new Set(copy.parts.filter((part) => part.isHidden).map((part) => part.id));
  const parts = songService
    .loadDuplicateParts(copy)
    .filter((part) => !part.isHidden && !(part.isDuplicate && hiddenIds.has(part.duplicateId)));
  const { durations, unit } = buildSongTimeline({ ...copy, parts });

  if (unit !== 'seconds') return null;

  return Object.values(durations).reduce((total, duration) => total + (duration.seconds || 0), 0);
};

/**
 * How long a run of songs plays for, back to back — only once every one of them has a length.
 * @param {Array} songs - The songs, off the team list or in full
 * @param {Map} lengths - Seconds by song id, from useSongLengths
 * @returns {number|null} - Seconds, or null while any song has no length
 */
export const totalSeconds = (songs = [], lengths = new Map()) =>
  songs.length > 0 && songs.every((song) => lengths.has(song._id))
    ? songs.reduce((total, song) => total + lengths.get(song._id), 0)
    : null;

/**
 * A length as the badges show it, to the nearest minute
 * @param {number} seconds
 * @returns {string} - "~24 min", "~1 h 05 min"
 */
export const formatServiceLength = (seconds) => {
  const minutes = Math.max(1, Math.round(seconds / 60));

  if (minutes < 60) return `~${minutes} min`;

  return `~${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
};
